import * as React from 'react';
import Typography from '@mui/material/Typography';
import { Alert, Box, Container, Pagination, PaginationItem, Snackbar } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LoaderOverlay } from '../utils/components';
import Sidebar2 from '../utils/SideBar2';
import { useAuth } from '../../contexts/AuthContext';
import { useTransactions } from './hooks/useTransactions';
import { useFilters } from './hooks/useFilters';
import { useAutoStatusUpdate } from './hooks/useAutoStatusUpdate';
import SummaryCards from './components/SummaryCards';
import TransactionTable from './components/TransactionTable';
import MobileCards from './components/MobileCards';
import ActiveFilters from './components/ActiveFilters';
import QuickSearch from './components/QuickSearch';
import ApiErrorHandler from '../ErrorBoundary/ApiErrorHandler';
import { url } from '../utils/constants';
import { toast } from 'sonner';

export default function PagamentoList({ usePageTitle }) {
    usePageTitle('PixTick | Transações');
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = React.useState(false);
    const [copied, setCopied] = React.useState(false);
    const [refreshingId, setRefreshingId] = React.useState(null);
    const { logout, user } = useAuth();

    // Usar os hooks customizados
    const { filters, setFilters, currentPage, filtrado, handlePageChange, handleFilter } = useFilters();
    const {
        transactions,
        loading,
        totalPages,
        idpdvs,
        amountPaid,
        amountPending,
        amountExpired,
        amountPaidF,
        amountPendingF,
        amountExpiredF,
        error,
        refetch,
        retry
    } = useTransactions(currentPage, filters, logout, navigate);

    // Hook para atualização automática de status
    useAutoStatusUpdate(transactions, refetch);

    const handleTransactionClick = (transaction) => {
        console.log(transaction);
        navigate(`/relatorio-pagamento/${transaction.id}`);
    };

    const handleCopyPixCode = (pixCode) => {
        navigator.clipboard.writeText(pixCode);
        setCopied(true);
        setTimeout(() => {
            setCopied(false);
        }, 2000);
    };

    const handleFilterWithRefetch = async () => {
        await handleFilter(refetch);
    };

    const handleQuickSearch = (searchTerm) => {
        // Implementar busca rápida
        console.log('Busca rápida:', searchTerm);
        // Aqui você pode implementar a lógica de busca
    };

    const handleClearSearch = () => {
        // Limpar busca
        console.log('Limpar busca');
    };

    const handleAtualizarStatusMercadoPago = async (transaction) => {
        const transactionId = transaction.id || transaction._id;
        if (!transactionId) return;
        setRefreshingId(transactionId);
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.get(
                `${url}/api/v1/pagamento/${transactionId}/atualizarStatusMercadoPago`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(data.message || (data.status === 'PAGO' ? 'Pagamento confirmado!' : 'Status consultado.'));
            refetch();
        } catch (err) {
            const msg = err.response?.data?.error || 'Erro ao atualizar status no Mercado Pago';
            toast.error(msg);
        } finally {
            setRefreshingId(null);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            backgroundColor: '#F5F5F5',
            padding: { xs: 1, md: 2 },
            paddingTop: { xs: 8, md: 10 },
            paddingBottom: { xs: 2, md: 3 }
        }}>
            {loading ? <LoaderOverlay loading={loading} /> : (
                <Container maxWidth="xl" sx={{ px: { xs: 0.5, md: 2 } }}>
                    <Grid container spacing={{ xs: 1.5, md: 2 }}>
                        {/* Título */}
                        <Grid item xs={12}>
                            <Box sx={{
                                textAlign: 'center',
                                mb: 2,
                                p: { xs: 2, md: 3 },
                                backgroundColor: 'white',
                                borderRadius: 3,
                                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                mt: 0
                            }}>
                                <Typography
                                    variant="h4"
                                    fontWeight="bold"
                                    fontFamily="Ubuntu"
                                    component="h1"
                                    sx={{
                                        color: '#1976D2',
                                        mb: 1,
                                        fontSize: { xs: '1.5rem', md: '2rem' },
                                        lineHeight: 1.3,
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    Relatório das Transações
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        fontSize: { xs: '0.9rem', md: '1rem' },
                                        lineHeight: 1.4
                                    }}
                                >
                                    Acompanhe todas as suas transações PIX em tempo real
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Cards de Resumo */}
                        <Grid item xs={12}>
                            <SummaryCards
                                amountPaid={amountPaid}
                                amountPending={amountPending}
                                amountExpired={amountExpired}
                                amountPaidF={amountPaidF}
                                amountPendingF={amountPendingF}
                                amountExpiredF={amountExpiredF}
                                filtrado={filtrado}
                                filters={filters}
                            />
                        </Grid>

                        {/* Busca Rápida */}
                        <Grid item xs={12}>
                            <QuickSearch
                                onSearch={handleQuickSearch}
                                onClear={handleClearSearch}
                                onFilterClick={() => setSidebarOpen(true)}
                            />
                        </Grid>

                        {/* Filtros Ativos */}
                        <Grid item xs={12}>
                            <ActiveFilters filtrado={filtrado} filters={filters} />
                        </Grid>

                        {/* Sidebar de Filtros */}
                        <Grid item xs={12} sx={{ display: { xs: 'flex' }, justifyContent: 'center' }}>
                            <Sidebar2
                                open={sidebarOpen}
                                setOpen={setSidebarOpen}
                                filters={filters}
                                setFilters={setFilters}
                                onFilter={handleFilterWithRefetch}
                                idpdvs={idpdvs}
                            />
                        </Grid>

                        {/* Lista de Transações */}
                        <Grid item xs={12}>
                            <Box sx={{
                                backgroundColor: 'white',
                                borderRadius: 3,
                                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                overflow: 'hidden',
                                minHeight: 400
                            }}>
                                {transactions.length > 0 ? (
                                    <>
                                        <TransactionTable
                                            transactions={transactions}
                                            onTransactionClick={handleTransactionClick}
                                            onCopyPixCode={handleCopyPixCode}
                                            isAdmin={user?.role === 'ADMIN'}
                                            onAtualizarStatusMercadoPago={handleAtualizarStatusMercadoPago}
                                            refreshingStatusId={refreshingId}
                                        />
                                        <MobileCards
                                            transactions={transactions}
                                            onTransactionClick={handleTransactionClick}
                                            onCopyPixCode={handleCopyPixCode}
                                            isAdmin={user?.role === 'ADMIN'}
                                            onAtualizarStatusMercadoPago={handleAtualizarStatusMercadoPago}
                                            refreshingStatusId={refreshingId}
                                        />
                                    </>
                                ) : (
                                    <Box sx={{
                                        p: { xs: 3, md: 4 },
                                        textAlign: 'center',
                                        color: 'text.secondary',
                                        minHeight: 300,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}>
                                        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
                                            Nenhum pagamento encontrado
                                        </Typography>
                                        <Typography variant="body1">
                                            Tente ajustar os filtros ou criar uma nova transação
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        {/* Paginação */}
                        {transactions.length > 0 && (
                            <Grid item xs={12}>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    p: 2,
                                    backgroundColor: 'white',
                                    borderRadius: 3,
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                }}>
                                    <Pagination
                                        count={totalPages}
                                        page={currentPage}
                                        onChange={handlePageChange}
                                        color="primary"
                                        size="large"
                                        renderItem={(item) => (
                                            <PaginationItem
                                                component="button"
                                                {...item}
                                                onClick={(event) => handlePageChange(event, item.page)}
                                            />
                                        )}
                                    />
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Container>
            )}

            {/* Snackbar para feedback de cópia */}
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={copied}
                autoHideDuration={2000}
                onClose={() => setCopied(false)}
            >
                <Alert onClose={() => setCopied(false)} severity="success" sx={{ width: '100%' }}>
                    Código copiado!
                </Alert>
            </Snackbar>

            {/* Handler de erros de API */}
            <ApiErrorHandler
                error={error}
                onRetry={retry}
            />

        </Box>
    );

}

